import React, { useState, useEffect } from "react";
import "../../styles/CheckoutButton.scss";

interface Props {
  formData: any;
  selectedTicket: any;
  quantity: number;
  selectedDate: string;
  allDates: string[];
}

const ProceedCheckoutButton: React.FC<Props> = ({
  formData,
  selectedTicket,
  quantity,
  selectedDate,
  allDates,
}) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
  const APP_KEY = process.env.REACT_APP_APP_KEY;

  useEffect(() => {
    const requiredFields = [
      "familyName",
      "givenName",
      "contactNumber",
      "mailingAddress",
      "email",
      "country",
      "insuranceCompany",
      "birthdate",
      "nationality",
    ];

    const allFilled = requiredFields.every((field) => {
      const value = formData[field];

      return typeof value === "string" && value.trim() !== "";
    });

    setIsFormValid(allFilled);
  }, [formData]);

  const handleCheckout = async () => {
    if (!selectedTicket || !selectedDate) {
      setMessage("Please select ticket and date first.");
      return;
    }

    if (!isFormValid) {
      setMessage("Please fill out all required fields.");
      return;
    }

    if (!isChecked) {
      setMessage("You must agree to the Terms, Conditions, and Policies.");
      return;
    }

    setLoading(true);
    setMessage("");

    const payload = {
      ticket_qty: String(quantity),

      customer_first_name: formData.givenName,
      customer_last_name: formData.familyName,
      customer_email: formData.email,

      nationality: formData.nationality,
      insurance: formData.insuranceCompany,

      // Birthdate
      birthdate: formData.birthdate,

      mailing_address: formData.mailingAddress,
      country: formData.country,
      passport_no: formData.passportNo,
      family_name: formData.familyName,
      selected_dates: allDates,
      note: formData.note,
    };

    try {
      const eventID = selectedTicket?.event_id;
      const ticketID = selectedTicket?.id;

      if (!eventID || !ticketID) {
        setMessage("Missing event or ticket information.");
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${API_ENDPOINT}/api/v1/events/${eventID}/tickets/${ticketID}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-AppKey": APP_KEY || "",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();

      if (data.is_success) {
        const paymentUrl =
          data.payment_url ||
          data.data?.payment_url ||
          data?.result?.payment_url;

        if (paymentUrl) {
          setMessage("Checkout successful! Redirecting to payment...");
          window.location.href = paymentUrl;
        } else {
          console.error("Payment URL not found in response:", data);
          setMessage("Payment URL not found. Please contact support.");
        }
      } else {
        setMessage("Checkout failed. Please try again.");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      setMessage(
        "An error occurred during checkout. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const isButtonDisabled =
    loading ||
    !isChecked ||
    !isFormValid ||
    !selectedTicket ||
    !selectedDate;

  return (
    <div className="proceed-checkout-container">
      <div className="checkbox-container">
        <label className="terms-checkbox">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
          />

          <span>
            I have read and understand the{" "}
            <a
              href="https://drive.google.com/file/d/1SBMa2CG-C2gOjOuQ1r45TGRMicLWJp5W/view"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Terms and Conditions
            </a>
            .
          </span>
        </label>
      </div>

      <button
        className="proceed-btn"
        onClick={handleCheckout}
        disabled={isButtonDisabled}
      >
        {loading ? <div className="spinner"></div> : "Proceed to Checkout"}
      </button>

      {message && (
        <p className="checkout-message">
          {message}
        </p>
      )}
    </div>
  );
};

export default ProceedCheckoutButton;